<template>
  <div>
    <h4 class="title is-4">
        <span>Бланк тестирования</span>
    </h4>
    <h6 class="subtitle is-6">{{ survey.title }}</h6>

    <div v-show="loadingComplete">

    <div v-show="users.length > 0">

    <p class="has-text-centered no-print">
        <small>Бланк {{ userIndex + 1 }} из {{ users.length }}</small>
    </p>

    <nav class="pagination is-centered no-print">
        <a
            class="pagination-previous"
            :disabled="previousUserDisabled"
            v-shortkey="['alt', 'arrowleft']"
            @shortkey="previousUser()"
            @click="previousUser">
            Предыдущий бланк
            <small class="is-hidden-touch">
            &nbsp;
            <i>Alt + <b-icon icon="arrow-left" size="is-small"></b-icon></i>
            </small>
        </a>
        <a
            class="pagination-next"
            :disabled="nextUserDisabled"
            v-shortkey="['alt', 'arrowright']"
            @shortkey="nextUser()"
            @click="nextUser">
            Следующий бланк
            <small class="is-hidden-touch">
            &nbsp;
            <i>Alt + <b-icon icon="arrow-right" size="is-small"></b-icon></i>
            </small>
        </a>
        <ul class="pagination-list">
            <b-select
                class="user-select"
                v-model="user"
                expanded>
                <option
                    v-for="option in users"
                    :key="option.alias"
                    :value="option">
                    {{ option.full_name }}
                </option>
            </b-select>
        </ul>
    </nav>

    <br>

    <p v-if="this.session">
      <small>
        <p>Продолжительность выполнения:</p>
        <p>
          <span>
          с {{ $moment(this.session.start_at).local().format("DD-MM-YYYY HH:mm:ss") }}
          </span>
          <span>
          по {{ $moment(this.session.end_at).local().format("DD-MM-YYYY HH:mm:ss") }}
          </span>
        </p>
      </small>
    </p>

    <p class="has-text-centered no-print">
        <small>
            <p>
            <span class="has-text-success" v-if="scoreFinalized">
                Бланк оценен
            </span>
            <span class="has-text-danger" v-else>
                Бланк не оценен
            </span>
            </p>
            <p>{{ resultString }} баллов ({{ resultPercentage }}%)</p>
        </small>
    </p>

    <div class="columns has-text-centered print-only">
        <div class="column">
            <p>{{ user.full_name }}</p>
        </div>
        <div class="column">
            <p>{{ resultString }} баллов ({{ resultPercentage }}%)</p>
        </div>
    </div>

    <div class="survey">
        <div class="answers" v-show="questions.length">
            <answer
                ref="answers"
                :survey="survey"
                @review="updateAnswer"
                v-for="index in questions.length"
                :can-edit="false"
                v-model="questions[index - 1]"
                :key="user.alias + index"
                is-quiz
                score-view
            />
        </div>
    </div>

    <div class="section has-text-centered no-print">
        <h4 class="is-size-4">
            <template v-if="scoreFinalized">
                <p>{{ resultString }} баллов ({{ resultPercentage }}%)</p>
            </template>
            <template v-else>
                <p>Необходимо оценить открытые вопросы</p>
            </template>
        </h4>
    </div>

    </div>

    <div v-if="users.length == 0">
        <div class="section has-text-centered">
            <p>Нет заполненных бланков</p>
            <br>
            <button class="button" @click="refresh">
                <b-icon icon="refresh"></b-icon>
                <span>Обновить</span>
            </button>
        </div>
    </div>

    </div>

    <b-loading :active="!loadingComplete"></b-loading>

  </div>
</template>

<style>
.user-select {
  width: 100%;
}
</style>

<script>
import toasts from "../../mixins/toasts";
import Answer from "../../survey/Answer";

let answerDebounceInterval = 1000;

export default Vue.extend({
  props: {
    survey: {
      type: Object,
      required: true
    }
  },
  mixins: [toasts],
  components: { Answer },
  data() {
    return {
      scoreBase: 10,
      loadingComplete: false,
      user: {
        alias: ""
      },
      session: null,
      users: [],
      questions: [],
      answers: []
    };
  },
  watch: {
    user: {
      deep: true,
      handler(user) {
        this.$router.replace({
          query: {
            user: this.user.alias,
          },
        });
        this.loadAnswers(user);
        this.loadSession(user);
      }
    }
  },
  computed: {
    scoreFinalized() {
      for (let answer of this.answers) {
        if (answer.score == null || answer.score < 0) {
          return false;
        }
      }
      return true;
    },
    maximumScore() {
      let result = _.reduce(
        this.questions,
        sum => {
          return sum + this.scoreBase;
        },
        0
      );

      switch (this.survey.score_type) {
        case "binary": {
          return result / this.scoreBase;
        }
        default: {
          return result;
        }
      }
    },
    currentScore() {
      return _.reduce(
        this.answers,
        (sum, answer) => {
          if (answer.score > -1) {
            return sum + this.transformScore(answer.score);
          } else {
            return sum;
          }
        },
        0
      );
    },
    resultPercentage() {
      return parseInt(100 * (this.currentScore / this.maximumScore));
    },
    resultString() {
      return `${this.currentScore} / ${this.maximumScore}`;
    },
    putAnswer() {
      return _.throttle(answer => {
        return axios
          .put(
            `/api/surveys/${this.survey.id}/answers/${answer.question}/${
              this.user.alias
            }`,
            {
              question: answer.question,
              score: answer.score,
              comment: answer.comment
            }
          )
          .catch(error => {
            this.toast("error", "@unexpected", error);
          });
      }, answerDebounceInterval);
    },
    userIndex() {
      return this.user
        ? _.findIndex(this.users, { alias: this.user.alias })
        : -1;
    },
    previousUserDisabled() {
      return this.userIndex <= 0;
    },
    nextUserDisabled() {
      return this.userIndex >= this.users.length - 1;
    }
  },
  created() {
    axios
      .get(`/api/surveys/${this.survey.id}/questions`)
      .then(response => {
        this.questions = response.data;

        axios
          .get(`/api/surveys/${this.survey.id}/participants`)
          .then(response => {
            this.users = _.sortBy(
              _.map(
                _.filter(response.data, {
                  participated: true
                }),
                "user"
              ),
              "full_name"
            );

            if (this.users.length > 0) {
              let user = null;
              let alias = this.$route.query.user;

              if (alias) {
                user = _.find(this.users, {
                  alias: alias
                });
              }

              this.user = user ? user : _.first(this.users);
            } 
              
            this.loadingComplete = true;
          })
          .catch(error => {
            this.toast("error", "@unexpected", error);
          });
      })
      .catch(error => {
        this.toast("error", "@unexpected", error);
      });
  },
  methods: {
    transformScore(score) {
      switch (this.survey.score_type) {
        case "binary": {
          return score < this.scoreBase ? 0 : 1;
        }
        default: {
          return score;
        }
      }
    },
    nextUser() {
      if (!this.loadingComplete) {
        return;
      }

      let user = this.users[this.userIndex + 1];
      if (user) {
        this.user = user;
      }
    },
    previousUser() {
      if (!this.loadingComplete) {
        return;
      }

      let user = this.users[this.userIndex - 1];
      if (user) {
        this.user = user;
      }
    },
    refresh() {
      location.reload();
    },
    updateAnswer(answer) {
      let index = _.findIndex(this.answers, {
        question: answer.question
      });

      if (index > -1) {
        this.answers.splice(index, 1, answer);
      } else {
        this.answers.push(answer);
      }

      this.putAnswer(answer);
    },
    loadAnswers(user) {
      this.loadingComplete = false;

      return axios
        .get(`/api/surveys/${this.survey.id}/answers/${user.alias}`)
        .then(response => {
          this.answers = _.get(response.data, "answers", []);
          this.refreshAnswers();
        })
        .catch(error => {
          this.toast("error", "@unexpected", error);
        })
        .finally(() => {
          this.loadingComplete = true;
        });
    },
    loadSession(user) {
      axios
      .get(`/api/surveys/${this.survey.id}/sessions/${user.alias}`)
      .then(response => {
        this.session = response.data;
      })
      .catch(error => {
        this.toast("error", "@unexpected", error);
      });
    },
    refreshAnswers() {
      _.each(this.$refs.answers, answer => {
        answer.update(
          _.find(this.answers, {
            question: answer.model.question
          })
        );
      });
      this.loadingComplete = true;
    }
  }
});
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/views/surveys/ScoreManager.vue